import config, { logger } from '../config';
import { normalizeCheckInHistory } from './checkInUtils';

/**
 * Returns headers for backend API requests.
 * @param {Object} additional - Additional headers to merge (e.g. { 'Content-Type': 'application/json' })
 */
export const getToken = () => localStorage.getItem(config.STORAGE_KEYS.TOKEN);

/**
 * Headers for backend API requests.
 * Session tokens are sent as Authorization: Bearer so they stay out of URLs.
 * @param {Object} additional - Additional headers to merge
 * @param {string|null|undefined} token - Explicit token. Omit to use localStorage.
 */
export const getApiHeaders = (additional = {}, token) => {
  const headers = { ...additional };
  const hasAuthorization = Object.keys(headers).some((key) => key.toLowerCase() === 'authorization');
  const resolvedToken = token === undefined ? getToken() : token;
  if (!hasAuthorization && resolvedToken) {
    headers.Authorization = `Bearer ${resolvedToken}`;
  }
  return headers;
};

export const apiFetch = (url, options = {}) => {
  const { headers, ...rest } = options;
  return fetch(url, {
    ...rest,
    referrerPolicy: rest.referrerPolicy || 'no-referrer',
    cache: rest.cache || 'no-store',
    headers: getApiHeaders(headers || {}),
  });
};

const NOT_AUTHENTICATED = { ok: false, message: 'Not authenticated.' };
const SESSION_EXPIRED = { ok: false, message: 'Session expired. Please sign in again.' };
const NETWORK_ERROR = { ok: false, message: 'Network error. Please try again.' };

const redirectToLogin = (navigate, isAdmin = false) => {
  localStorage.clear();
  const redirectPath = isAdmin ? '/admin-login' : '/login';
  if (navigate) navigate(redirectPath);
  else window.location.href = redirectPath;
};

const parseJsonResponse = (response) => response.json().catch(() => ({}));

const mapOnboardingAiChatPayload = (payload) => ({
  chatData: payload?.v2_onboarding_ai?.chat || [],
  onboardingAiResult: payload?.v2_onboarding_ai_result ?? null,
  checkupAndActionPlan: payload?.onboarding_ai_checkup_and_action_plan ?? null,
});

const putUserMutation = async (
  endpoint,
  bodyFields,
  navigate,
  { logLabel, successMessage, failureMessage },
) => {
  const token = getToken();
  if (!token) return NOT_AUTHENTICATED;

  try {
    const response = await apiFetch(`${config.API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ token, ...bodyFields }),
    });

    if (handleApiResponseError(response, navigate, false)) {
      return SESSION_EXPIRED;
    }

    const data = await parseJsonResponse(response);
    if (response.ok && data?.success !== false) {
      return { ok: true, message: data?.message || successMessage };
    }
    return { ok: false, message: data?.message || failureMessage };
  } catch (error) {
    logger.error(logLabel, error);
    handleApiError(error, navigate, false);
    return NETWORK_ERROR;
  }
};

const putTokenQueryFlag = async (endpoint, logLabel) => {
  const token = getToken();
  if (!token) return false;

  try {
    const url = `${config.API_BASE_URL}${endpoint}`;
    const response = await apiFetch(url, {
      method: 'PUT',
      headers: getApiHeaders(),
    });
    if (!response.ok) return false;
    const data = await parseJsonResponse(response);
    return Boolean(data?.success ?? response.ok);
  } catch (error) {
    logger.error(logLabel, error);
    return false;
  }
};

const submitAdminForm = async (
  endpoint,
  formData,
  navigate,
  { method = 'PUT', logLabel, successMessage, failureMessage },
) => {
  try {
    const response = await apiFetch(`${config.API_BASE_URL}${endpoint}`, {
      method,
      headers: getApiHeaders(),
      body: formData,
    });

    if (handleApiResponseError(response, navigate, true)) {
      return SESSION_EXPIRED;
    }

    const data = await parseJsonResponse(response);
    if (response.ok && data.success) {
      return { ok: true, message: data.message || successMessage, data: data.data };
    }
    return { ok: false, message: data.message || failureMessage };
  } catch (error) {
    logger.error(logLabel, error);
    handleApiError(error, navigate, true);
    return NETWORK_ERROR;
  }
};

const submitUserForm = async (
  endpoint,
  formData,
  navigate,
  { method = 'PUT', logLabel, successMessage, failureMessage },
) => {
  try {
    const response = await apiFetch(`${config.API_BASE_URL}${endpoint}`, {
      method,
      headers: getApiHeaders(),
      body: formData,
    });

    if (handleApiResponseError(response, navigate, false)) {
      return SESSION_EXPIRED;
    }

    const data = await parseJsonResponse(response);
    if (response.ok && data?.success !== false) {
      return { ok: true, message: data?.message || successMessage, data: data?.data };
    }
    return { ok: false, message: data?.message || failureMessage };
  } catch (error) {
    logger.error(logLabel, error);
    handleApiError(error, navigate, false);
    return NETWORK_ERROR;
  }
};

export const handleApiError = (error, navigate, isAdmin = false) => {
  logger.error('API Error:', error);
  redirectToLogin(navigate, isAdmin);
};

export const handleApiResponseError = (response, navigate, isAdmin = false) => {
  if (response.status === 401) {
    redirectToLogin(navigate, isAdmin);
    return true;
  }

  if (!response.ok) {
    logger.error(`HTTP error! status: ${response.status}`);
    return false;
  }

  return false;
};

/**
 * Fetches the list of CFP experts from the backend.
 * GET /api/user/experts  (no auth required)
 *
 * @param {AbortSignal|null} [signal]
 * @returns {Promise<Array|null>} Array of expert objects, or null on error.
 */
export const fetchExperts = async (signal = null) => {
  try {
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_GET_EXPERTS}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      ...(signal && { signal }),
    });
    if (!response.ok) {
      logger.error('Failed to fetch experts, status:', response.status);
      return null;
    }
    const data = await response.json().catch(() => ({}));
    if (!data?.success) return null;
    return Array.isArray(data?.data?.experts) ? data.data.experts : null;
  } catch (error) {
    if (error?.name === 'AbortError') return null;
    logger.error('Error fetching experts:', error);
    return null;
  }
};

// API function to update user name (for OnboardingInitial page)
export const updateUserName = async (token, name, navigate, isAdmin = false) => {
  try {
    const response = await apiFetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.USER_UPDATE_NAME}`, {
      method: 'PUT',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        token,
        name
      }),
    });

    // Handle response errors consistently
    if (handleApiResponseError(response, navigate, isAdmin)) {
      return null; // Error was handled, return null
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logger.error('Error updating user name:', error);
    handleApiError(error, navigate, isAdmin);
    throw error;
  }
};

/**
 * Updates the display name without redirecting on failure.
 * Used when a signup flow already created the account.
 *
 * @param {string} token
 * @param {string} name
 * @returns {Promise<boolean>}
 */
export const tryUpdateUserName = async (token, name) => {
  try {
    const response = await apiFetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.USER_UPDATE_NAME}`, {
      method: 'PUT',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ token, name }),
    });
    if (!response.ok) {
      logger.error('Could not save confirmed name:', response.status);
      return false;
    }
    return true;
  } catch (error) {
    logger.error('Could not save confirmed name:', error);
    return false;
  }
};

/**
 * Fetches account settings (personal info, account status, coach connectivity).
 * GET /api/user/settings
 *
 * @param {function|null|undefined} navigate
 * @param {AbortSignal|null} [signal]
 * @returns {Promise<object|null>}
 */
export const fetchUserSettings = async (navigate, signal = null) => {
  const token = getToken();
  if (!token) return null;

  try {
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_GET_SETTINGS}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      ...(signal && { signal }),
    });

    if (handleApiResponseError(response, navigate, false)) {
      return null;
    }

    const data = await parseJsonResponse(response);
    if (!response.ok || !data?.success) {
      return null;
    }

    return data?.data ?? null;
  } catch (error) {
    if (error?.name === 'AbortError') return null;
    logger.error('Error fetching user settings:', error);
    return null;
  }
};

/**
 * Updates the authenticated user's full name and phone number.
 * PUT /api/user/update-profile
 *
 * @param {{ name: string, phone?: string }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export const updateUserProfile = async ({ name, phone = '' }, navigate) => {
  const token = getToken();
  if (!token) return NOT_AUTHENTICATED;

  try {
    const response = await apiFetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.USER_UPDATE_PROFILE}`, {
      method: 'PUT',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ token, name, phone }),
    });

    if (handleApiResponseError(response, navigate, false)) {
      return SESSION_EXPIRED;
    }

    const data = await parseJsonResponse(response);
    if (response.ok && data?.success !== false) {
      return {
        ok: true,
        message: data?.message || 'Profile updated successfully',
        data: data?.data,
      };
    }
    return { ok: false, message: data?.message || 'Failed to update profile' };
  } catch (error) {
    logger.error('Error updating user profile:', error);
    handleApiError(error, navigate, false);
    return NETWORK_ERROR;
  }
};

/**
 * Stores the phone number submitted in the tawk.to pre-chat or offline form.
 * PUT /api/user/update-phone
 *
 * @param {string} phone
 * @returns {Promise<{ ok: boolean, message?: string, data?: object }>}
 */
export const updateUserPhone = async (phone) => {
  const token = getToken();
  const normalized = String(phone ?? '').trim();
  if (!token || !normalized) return { ok: false, message: 'Not authenticated.' };

  try {
    const response = await apiFetch(
      `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_UPDATE_PHONE}`,
      {
        method: 'PUT',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ token, phone: normalized }),
      },
    );
    const data = await parseJsonResponse(response);
    if (response.ok && data?.success !== false) {
      return { ok: true, data: data?.data };
    }
    return { ok: false, message: data?.message || 'Failed to update phone' };
  } catch (error) {
    logger.error('Error updating user phone:', error);
    return NETWORK_ERROR;
  }
};

/**
 * Changes the authenticated user's password.
 * PUT /api/user/change-password
 *
 * @param {{ oldPassword: string, newPassword: string, confirmPassword: string }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, message?: string, data?: object }>}
 */
export const changeUserPassword = async (
  { oldPassword, newPassword, confirmPassword },
  navigate,
) => {
  const token = getToken();
  if (!token) return NOT_AUTHENTICATED;

  try {
    const response = await apiFetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.USER_CHANGE_PASSWORD}`, {
      method: 'PUT',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        token,
        old_password: oldPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),
    });

    if (handleApiResponseError(response, navigate, false)) {
      return SESSION_EXPIRED;
    }

    const data = await parseJsonResponse(response);
    if (response.ok && data?.success !== false) {
      return {
        ok: true,
        message: data?.message || 'Password changed successfully',
        data: data?.data,
      };
    }
    return { ok: false, message: data?.message || 'Failed to change password' };
  } catch (error) {
    logger.error('Error changing user password:', error);
    handleApiError(error, navigate, false);
    return NETWORK_ERROR;
  }
};

/**
 * Deactivates the authenticated user's account (sets status to deleted).
 * PUT /api/user/deactivate-account
 *
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export const deactivateUserAccount = async (navigate) => {
  const token = getToken();
  if (!token) return NOT_AUTHENTICATED;

  try {
    const response = await apiFetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.USER_DEACTIVATE_ACCOUNT}`, {
      method: 'PUT',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ token }),
    });

    if (handleApiResponseError(response, navigate, false)) {
      return SESSION_EXPIRED;
    }

    const data = await parseJsonResponse(response);
    if (response.ok && data?.success !== false) {
      return { ok: true, message: data?.message || 'Account deactivated successfully' };
    }
    return { ok: false, message: data?.message || 'Failed to deactivate account' };
  } catch (error) {
    logger.error('Error deactivating user account:', error);
    return NETWORK_ERROR;
  }
};

/**
 * Checks whether a verified Google identity already has an account.
 * POST /api/login/google/exists
 *
 * Does not create a user. Call only after Google authentication succeeds.
 *
 * @param {string} accessToken Google access token
 * @returns {Promise<{ ok: boolean, exists?: boolean, message?: string }>}
 */
export const checkGoogleUserExists = async (accessToken) => {
  try {
    const response = await apiFetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.GOOGLE_USER_EXISTS}`, {
      method: 'POST',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ access_token: accessToken }),
    });

    const data = await parseJsonResponse(response);
    if (response.ok && data?.success !== false) {
      return { ok: true, exists: Boolean(data?.data?.exists) };
    }
    return { ok: false, message: data?.message || 'Could not check this account.' };
  } catch (error) {
    logger.error('Error checking if Google user exists:', error);
    return NETWORK_ERROR;
  }
};

/**
 * Saves optional feedback after account deactivation.
 * PUT /api/user/deleted-reason
 *
 * @param {{ token: string, deletedReason: string }} params
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export const updateUserDeletedReason = async ({ token, deletedReason }) => {
  const tokenValue = String(token ?? '').trim();
  const reason = String(deletedReason ?? '').trim();
  if (!tokenValue) return NOT_AUTHENTICATED;
  if (!reason) return { ok: false, message: 'Deletion reason is required.' };

  try {
    const response = await apiFetch(
      `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_UPDATE_DELETED_REASON}`,
      {
        method: 'PUT',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ token: tokenValue, deleted_reason: reason }),
      },
    );

    const data = await parseJsonResponse(response);
    if (response.ok && data?.success !== false) {
      return { ok: true, message: data?.message || 'Thank you for your feedback.' };
    }
    return { ok: false, message: data?.message || 'Failed to submit feedback.' };
  } catch (error) {
    logger.error('Error updating deleted reason:', error);
    return NETWORK_ERROR;
  }
};

/**
 * Fetches onboarding AI chat data and result for a user (admin).
 * GET /api/admin/onboarding-ai (response `data.v2_onboarding_ai` for chat blob).
 * Used by admin table and Checkup and Plan page.
 * @param {string} username - User to fetch for
 * @returns {Promise<{ chatData: array, onboardingAiResult: object|null, checkupAndActionPlan?: unknown }|null>}
 */
export const fetchAdminOnboardingAIChatData = async (username) => {
  const token = getToken();
  if (!token || !username) return null;
  try {
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.ADMIN_ONBOARDING_AI}?username=${encodeURIComponent(username)}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (!data.success || !data.data) return null;
    return mapOnboardingAiChatPayload(data.data);
  } catch (error) {
    logger.error('Error fetching admin onboarding AI chat data:', error);
    return null;
  }
};

/**
 * Fetches content reflections and the saved onboarding result.
 * GET /api/user/get-quick-richuels-and-check-in-history?token=...
 *
 * @returns {Promise<{
 *   content_reflections: Array,
 *   v2_onboarding_ai_result: Object|null
 * }|null>}
 * @param {AbortSignal|null} [signal]
 */
export const fetchQuickRichuelsAndCheckInHistory = async (signal = null) => {
  const token = getToken();
  if (!token) return null;
  try {
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_GET_QUICK_RICHUELS_AND_CHECK_IN_HISTORY}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      ...(signal && { signal }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (!data?.success) return null;
    const d = data?.data ?? {};
    return {
      content_reflections: normalizeCheckInHistory(d?.content_reflections),
      v2_onboarding_ai_result: d?.v2_onboarding_ai_result ?? null,
    };
  } catch (error) {
    if (error?.name === 'AbortError') return null;
    logger.error('Error fetching content reflections:', error);
    return null;
  }
};

/**
 * Saves confidence, anxiety, and the local date for one finished content.
 * PUT /api/user/content/reflection — body: token, content_id, confidence, anxiety, reflected_on
 *
 * @param {{ contentId: string, confidence: number, anxiety: number, reflectedOn: string }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, message?: string, content?: object }>}
 */
export const saveContentReflection = ({ contentId, confidence, anxiety, reflectedOn }, navigate) =>
  putContentProgress(
    config.API_ENDPOINTS.USER_SAVE_CONTENT_REFLECTION,
    {
      content_id: String(contentId ?? '').trim(),
      confidence,
      anxiety,
      reflected_on: reflectedOn,
    },
    navigate,
    'Could not save this reflection.',
  );

const buildCfpSessionTopic = ({ topic, tabLabel, topicCardTitle }) => {
  if (topic) return String(topic).trim();
  const label = String(tabLabel ?? '').trim();
  const title = String(topicCardTitle ?? '').trim();
  if (label && title) return `${label} - ${title}`;
  return title || label;
};

/**
 * Creates a CFP session row with the selected topic.
 * PUT /api/user/insert-cfp-session — body: token, topic, expert_id
 *
 * @param {{ topic?: string, tabLabel?: string, topicCardTitle?: string, expertId: string }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export const insertUserCfpSession = ({ topic, tabLabel, topicCardTitle, expertId }, navigate) =>
  putUserMutation(
    config.API_ENDPOINTS.USER_INSERT_CFP_SESSION,
    { topic: buildCfpSessionTopic({ topic, tabLabel, topicCardTitle }), expert_id: expertId },
    navigate,
    {
      logLabel: 'Error updating user CFP session:',
      successMessage: 'CFP session saved successfully.',
      failureMessage: 'Failed to save CFP session selection.',
    },
  );

/**
 * Checks whether the authenticated user already has a CFP session / used Initial.
 * GET /api/user/cfp-session-is-existing?token=...
 *
 * @param {function|null|undefined} navigate
 * @param {AbortSignal|null} [signal]
 * @returns {Promise<{ isExisting: boolean, cfpInitialSessionDone: boolean, mustUseCheckout: boolean }|null>}
 */
export const fetchUserCfpSessionIsExisting = async (navigate, signal = null) => {
  const token = getToken();
  if (!token) return null;
  try {
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_CFP_SESSION_IS_EXISTING}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      ...(signal && { signal }),
    });
    if (handleApiResponseError(response, navigate, false)) {
      return null;
    }
    const data = await response.json().catch(() => ({}));
    if (!data?.success) return null;
    const isExisting = Boolean(data?.data?.is_existing);
    const cfpInitialSessionDone = Boolean(data?.data?.cfp_initial_session_done);
    return {
      isExisting,
      cfpInitialSessionDone,
      // After Initial is done (or any session exists), schedule via paid checkout
      mustUseCheckout: isExisting || cfpInitialSessionDone,
    };
  } catch (error) {
    if (error?.name === 'AbortError') return null;
    logger.error('Error checking user CFP session existence:', error);
    handleApiError(error, navigate, false);
    return null;
  }
};

/**
 * Fetches the authenticated user's CFP sessions.
 * GET /api/user/cfp-sessions?token=...
 *
 * @param {function|null|undefined} navigate
 * @param {AbortSignal|null} [signal]
 * @returns {Promise<{ ok: boolean, sessions: Array, message?: string }>}
 */
export const fetchUserCfpSessions = async (navigate, signal = null) => {
  const token = getToken();
  if (!token) {
    return { ok: false, message: 'Not authenticated.', sessions: [] };
  }

  try {
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_GET_CFP_SESSIONS}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      ...(signal && { signal }),
    });

    if (handleApiResponseError(response, navigate, false)) {
      return { ok: false, message: 'Session expired. Please sign in again.', sessions: [] };
    }

    const data = await response.json().catch(() => ({}));
    if (response.ok && data.success) {
      return { ok: true, sessions: data.data || [] };
    }
    return { ok: false, message: data.message || 'Failed to fetch CFP sessions', sessions: [] };
  } catch (error) {
    if (error?.name === 'AbortError') {
      return { ok: false, message: 'Request cancelled.', sessions: [] };
    }
    logger.error('Error fetching user CFP sessions:', error);
    handleApiError(error, navigate, false);
    return { ok: false, message: 'Network error. Please try again.', sessions: [] };
  }
};

/**
 * Soft-deletes a CFP session for the authenticated user.
 * POST /api/user/delete-cfp-session — body: token, id
 *
 * @param {{ id: string }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export const deleteUserCfpSession = async ({ id }, navigate) => {
  const token = getToken();
  const idStr = String(id ?? '').trim();
  if (!token) {
    return { ok: false, message: 'Not authenticated.' };
  }
  if (!idStr) {
    return { ok: false, message: 'Session id is required.' };
  }

  try {
    const response = await apiFetch(
      `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_DELETE_CFP_SESSION}`,
      {
        method: 'POST',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ token, id: idStr }),
      },
    );

    if (handleApiResponseError(response, navigate, false)) {
      return { ok: false, message: 'Session expired. Please sign in again.' };
    }

    const data = await parseJsonResponse(response);
    if (response.ok && data?.success !== false) {
      return { ok: true, message: data?.message || 'CFP session deleted successfully.' };
    }
    return { ok: false, message: data?.message || 'Failed to delete CFP session.' };
  } catch (error) {
    logger.error('Error deleting user CFP session:', error);
    handleApiError(error, navigate, false);
    return { ok: false, message: 'Network error. Please try again.' };
  }
};

/**
 * Updates the topic on a CFP session for the authenticated user.
 * PUT /api/user/edit-cfp-session — body: token, id, topic
 *
 * @param {{ id: string, topic?: string, tabLabel?: string, topicCardTitle?: string }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export const editUserCfpSession = async ({ id, topic, tabLabel, topicCardTitle }, navigate) => {
  const token = getToken();
  const idStr = String(id ?? '').trim();
  if (!token) {
    return { ok: false, message: 'Not authenticated.' };
  }
  if (!idStr) {
    return { ok: false, message: 'Session id is required.' };
  }

  const sessionTopic = buildCfpSessionTopic({ topic, tabLabel, topicCardTitle });

  try {
    const response = await apiFetch(
      `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_EDIT_CFP_SESSION}`,
      {
        method: 'PUT',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ token, id: idStr, topic: sessionTopic }),
      },
    );

    if (handleApiResponseError(response, navigate, false)) {
      return { ok: false, message: 'Session expired. Please sign in again.' };
    }

    const data = await parseJsonResponse(response);
    if (response.ok && data?.success !== false) {
      return { ok: true, message: data?.message || 'CFP session updated successfully.' };
    }
    return { ok: false, message: data?.message || 'Failed to update CFP session.' };
  } catch (error) {
    logger.error('Error updating user CFP session:', error);
    handleApiError(error, navigate, false);
    return { ok: false, message: 'Network error. Please try again.' };
  }
};

/**
 * Fetches PDF documents for a CFP session.
 * GET /api/user/cfp-session-documents?token=...&cfp_session_id=...
 *
 * @param {{ cfpSessionId: string }} params
 * @param {function|null|undefined} navigate
 * @param {AbortSignal|null} [signal]
 * @returns {Promise<{ ok: boolean, documents: Array, message?: string }>}
 */
export const fetchUserCfpSessionDocuments = async ({ cfpSessionId }, navigate, signal = null) => {
  const token = getToken();
  const sessionId = String(cfpSessionId ?? '').trim();
  if (!token) {
    return { ok: false, message: 'Not authenticated.', documents: [] };
  }
  if (!sessionId) {
    return { ok: false, message: 'Session id is required.', documents: [] };
  }

  try {
    const params = new URLSearchParams({ cfp_session_id: sessionId });
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_GET_CFP_SESSION_DOCUMENTS}?${params.toString()}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      ...(signal && { signal }),
    });

    if (handleApiResponseError(response, navigate, false)) {
      return { ok: false, message: 'Session expired. Please sign in again.', documents: [] };
    }

    const data = await response.json().catch(() => ({}));
    if (response.ok && data.success) {
      return { ok: true, documents: data.data || [] };
    }
    return { ok: false, message: data.message || 'Failed to fetch documents', documents: [] };
  } catch (error) {
    if (error?.name === 'AbortError') {
      return { ok: false, message: 'Request cancelled.', documents: [] };
    }
    logger.error('Error fetching CFP session documents:', error);
    handleApiError(error, navigate, false);
    return { ok: false, message: 'Network error. Please try again.', documents: [] };
  }
};

/**
 * Creates a PDF document for a CFP session.
 * PUT /api/user/cfp-session-document/create — form: token, cfp_session_id, title, document
 *
 * @param {{ cfpSessionId: string, title: string, documentFile: File }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, message?: string, data?: unknown }>}
 */
export const createUserCfpSessionDocument = async ({ cfpSessionId, title, documentFile }, navigate) => {
  const token = getToken();
  const sessionId = String(cfpSessionId ?? '').trim();
  const titleTrim = String(title ?? '').trim();
  if (!token) {
    return { ok: false, message: 'Not authenticated.' };
  }
  if (!sessionId || !titleTrim || !documentFile) {
    return { ok: false, message: 'Title and PDF document are required.' };
  }

  const formData = new FormData();
  formData.append('token', token);
  formData.append('cfp_session_id', sessionId);
  formData.append('title', titleTrim);
  formData.append('document', documentFile);

  return submitUserForm(
    config.API_ENDPOINTS.USER_CREATE_CFP_SESSION_DOCUMENT,
    formData,
    navigate,
    {
      logLabel: 'Error creating CFP session document:',
      successMessage: 'Document uploaded successfully.',
      failureMessage: 'Failed to upload document.',
    },
  );
};

/**
 * Updates a CFP session document title, PDF file, or both.
 * PUT /api/user/cfp-session-document/update — form: token, id, title?, document?
 *
 * @param {{ id: string, title?: string, documentFile?: File|null }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, message?: string, data?: unknown }>}
 */
export const updateUserCfpSessionDocument = async ({ id, title, documentFile = null }, navigate) => {
  const token = getToken();
  const idStr = String(id ?? '').trim();
  const titleTrim = title != null ? String(title).trim() : '';
  if (!token) {
    return { ok: false, message: 'Not authenticated.' };
  }
  if (!idStr) {
    return { ok: false, message: 'Document id is required.' };
  }
  if (!titleTrim && !documentFile) {
    return { ok: false, message: 'Provide a title and/or PDF document to update.' };
  }

  const formData = new FormData();
  formData.append('token', token);
  formData.append('id', idStr);
  if (titleTrim) formData.append('title', titleTrim);
  if (documentFile) formData.append('document', documentFile);

  return submitUserForm(
    config.API_ENDPOINTS.USER_UPDATE_CFP_SESSION_DOCUMENT,
    formData,
    navigate,
    {
      logLabel: 'Error updating CFP session document:',
      successMessage: 'Document updated successfully.',
      failureMessage: 'Failed to update document.',
    },
  );
};

/**
 * Deletes a CFP session document.
 * POST /api/user/cfp-session-document/delete — body: token, id
 *
 * @param {{ id: string }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export const deleteUserCfpSessionDocument = async ({ id }, navigate) => {
  const token = getToken();
  const idStr = String(id ?? '').trim();
  if (!token) {
    return { ok: false, message: 'Not authenticated.' };
  }
  if (!idStr) {
    return { ok: false, message: 'Document id is required.' };
  }

  try {
    const response = await apiFetch(
      `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_DELETE_CFP_SESSION_DOCUMENT}`,
      {
        method: 'POST',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ token, id: idStr }),
      },
    );

    if (handleApiResponseError(response, navigate, false)) {
      return { ok: false, message: 'Session expired. Please sign in again.' };
    }

    const data = await parseJsonResponse(response);
    if (response.ok && data?.success !== false) {
      return { ok: true, message: data?.message || 'Document deleted successfully.' };
    }
    return { ok: false, message: data?.message || 'Failed to delete document.' };
  } catch (error) {
    logger.error('Error deleting CFP session document:', error);
    handleApiError(error, navigate, false);
    return { ok: false, message: 'Network error. Please try again.' };
  }
};

/**
 * Creates a Stripe Checkout Session for a paid CFP follow-up session.
 * POST /api/user/create-cfp-session-checkout — body: token, topic, expert_id
 *
 * @param {{ topic?: string, tabLabel?: string, topicCardTitle?: string, expertId: string }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, checkoutUrl?: string|null, message?: string }>}
 */
/**
 * Creates a Stripe Checkout Session for an existing CFP session awaiting payment.
 * POST /api/user/pay-cfp-session-checkout — body: token, id
 *
 * @param {{ id: string }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, checkoutUrl?: string|null, message?: string }>}
 */
export const payCfpSessionCheckout = async ({ id }, navigate) => {
  const token = getToken();
  const idStr = String(id ?? '').trim();
  if (!token) {
    return { ok: false, checkoutUrl: null, message: 'Not authenticated.' };
  }
  if (!idStr) {
    return { ok: false, checkoutUrl: null, message: 'Session id is required.' };
  }

  try {
    const response = await apiFetch(
      `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_PAY_CFP_SESSION_CHECKOUT}`,
      {
        method: 'POST',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ token, id: idStr }),
      },
    );

    if (handleApiResponseError(response, navigate, false)) {
      return { ok: false, checkoutUrl: null, message: 'Session expired. Please sign in again.' };
    }

    const data = await response.json().catch(() => ({}));
    if (response.ok && data?.success) {
      return {
        ok: true,
        checkoutUrl: data?.data?.checkout_url ?? null,
        message: data?.message,
      };
    }

    return {
      ok: false,
      checkoutUrl: null,
      message: data?.message || 'Failed to start checkout.',
    };
  } catch (error) {
    logger.error('Error paying CFP session checkout:', error);
    handleApiError(error, navigate, false);
    return { ok: false, checkoutUrl: null, message: 'Network error. Please try again.' };
  }
};

export const createCfpSessionCheckout = async (
  { topic, tabLabel, topicCardTitle, expertId },
  navigate,
) => {
  const token = getToken();
  if (!token) {
    return { ok: false, checkoutUrl: null, message: 'Not authenticated.' };
  }

  const sessionTopic = buildCfpSessionTopic({ topic, tabLabel, topicCardTitle });

  try {
    const response = await apiFetch(
      `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_CREATE_CFP_SESSION_CHECKOUT}`,
      {
        method: 'POST',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          token,
          topic: sessionTopic,
          expert_id: expertId,
        }),
      },
    );

    if (handleApiResponseError(response, navigate, false)) {
      return { ok: false, checkoutUrl: null, message: 'Session expired. Please sign in again.' };
    }

    const data = await response.json().catch(() => ({}));
    if (response.ok && data?.success) {
      return {
        ok: true,
        checkoutUrl: data?.data?.checkout_url ?? null,
        message: data?.message,
      };
    }

    return {
      ok: false,
      checkoutUrl: null,
      message: data?.message || 'Failed to start checkout.',
    };
  } catch (error) {
    logger.error('Error creating CFP session checkout:', error);
    handleApiError(error, navigate, false);
    return { ok: false, checkoutUrl: null, message: 'Network error. Please try again.' };
  }
};

/**
 * Fetches catalog categories with active content counts for the authenticated user.
 * GET /api/user/categories?token=...
 *
 * @param {function|null|undefined} navigate
 * @param {AbortSignal|null} [signal]
 * @returns {Promise<{ categories: Array<{ id?: string, category: string, name?: string, cover_image?: string, content_count: number }>, total_contents: number, recommended_category?: string|null }|null>}
 */
export const fetchUserCategories = async (navigate, signal = null) => {
  const token = getToken();
  if (!token) return null;
  try {
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_GET_CATEGORIES}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      ...(signal && { signal }),
    });
    if (handleApiResponseError(response, navigate, false)) {
      return null;
    }
    const data = await response.json().catch(() => ({}));
    if (!data?.success) return null;
    const d = data?.data ?? {};
    const categories = Array.isArray(d?.categories) ? d.categories : [];
    return {
      categories,
      total_contents: Number(d?.total_contents) || 0,
      recommended_category: String(d?.recommended_category ?? '').trim() || null,
    };
  } catch (error) {
    if (error?.name === 'AbortError') return null;
    logger.error('Error fetching user categories:', error);
    return null;
  }
};

/**
 * Fetches active catalog contents.
 * GET /api/user/contents?token=...&category_id=...
 * Omit category_id to load every active content.
 * Pass excludeNotStarted to omit contents this user has not started.
 *
 * @param {function|null|undefined} navigate
 * @param {{ categoryId?: string, contentId?: string, excludeNotStarted?: boolean, signal?: AbortSignal|null }} [params]
 * @returns {Promise<{ contents: Array, total_count: number }|null>}
 */
export const fetchUserContents = async (navigate, { categoryId, contentId, excludeNotStarted = false, signal = null } = {}) => {
  const token = getToken();
  const categoryIdTrim = String(categoryId ?? '').trim();
  const contentIdTrim = String(contentId ?? '').trim();
  if (!token) return null;
  try {
    const params = new URLSearchParams();
    if (categoryIdTrim) params.set('category_id', categoryIdTrim);
    if (contentIdTrim) params.set('content_id', contentIdTrim);
    if (excludeNotStarted) params.set('exclude_not_started', 'true');
    const query = params.toString();
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_GET_CONTENTS}${query ? `?${query}` : ''}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      ...(signal && { signal }),
    });
    if (handleApiResponseError(response, navigate, false)) {
      return null;
    }
    const data = await response.json().catch(() => ({}));
    if (!data?.success) return null;
    const contents = Array.isArray(data?.data?.contents) ? data.data.contents : [];
    return {
      contents,
      total_count: Number(data?.data?.total_count) || contents.length,
    };
  } catch (error) {
    if (error?.name === 'AbortError') return null;
    logger.error('Error fetching user contents:', error);
    return null;
  }
};

const putContentProgress = async (endpoint, bodyFields, navigate, failureMessage) => {
  const token = getToken();
  if (!token) return NOT_AUTHENTICATED;
  try {
    const response = await apiFetch(`${config.API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ token, ...bodyFields }),
    });
    if (handleApiResponseError(response, navigate, false)) {
      return SESSION_EXPIRED;
    }
    const data = await parseJsonResponse(response);
    if (response.ok && data?.success === true) {
      return { ok: true, message: data?.message || '', content: data?.data || null };
    }
    return { ok: false, message: data?.message || failureMessage };
  } catch (error) {
    logger.error('Error saving content progress:', error);
    handleApiError(error, navigate, false);
    return NETWORK_ERROR;
  }
};

export const watchUserContent = (navigate, contentId) =>
  putContentProgress(
    config.API_ENDPOINTS.USER_WATCH_CONTENT,
    { content_id: String(contentId ?? '').trim() },
    navigate,
    'Could not save that you finished this video.',
  );

export const checkUserContentAction = (navigate, contentId, actionIndex) =>
  putContentProgress(
    config.API_ENDPOINTS.USER_CHECK_CONTENT_ACTION,
    { content_id: String(contentId ?? '').trim(), action_index: actionIndex },
    navigate,
    'Could not check this action.',
  );

export const uncheckUserContentAction = (navigate, contentId, actionIndex) =>
  putContentProgress(
    config.API_ENDPOINTS.USER_UNCHECK_CONTENT_ACTION,
    { content_id: String(contentId ?? '').trim(), action_index: actionIndex },
    navigate,
    'Could not uncheck this action.',
  );

/**
 * Fetches categories (admin).
 * GET /api/admin/categories?token=...
 *
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ categories: Array, total_contents: number }|null>}
 */
export const fetchAdminCategories = async (navigate) => {
  const token = getToken();
  if (!token) return null;
  try {
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.ADMIN_GET_CATEGORIES}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
    });
    if (handleApiResponseError(response, navigate, true)) {
      return null;
    }
    const data = await parseJsonResponse(response);
    if (!data?.success) return null;
    const d = data?.data ?? {};
    return {
      categories: Array.isArray(d?.categories) ? d.categories : [],
      total_contents: Number(d?.total_contents) || 0,
    };
  } catch (error) {
    logger.error('Error fetching admin categories:', error);
    handleApiError(error, navigate, true);
    return null;
  }
};

/**
 * Creates a category (admin). Cover image is required.
 * PUT /api/admin/category/create — fields: token, cover_image, name
 *
 * @param {{ coverImageFile: File, name: string }} params
 * @param {function} navigate
 * @returns {Promise<{ ok: boolean, message?: string, data?: unknown }>}
 */
export const createAdminCategory = async (
  { coverImageFile, name },
  navigate,
) => {
  const token = getToken();
  const nameTrim = String(name ?? '').trim();
  if (!token || !coverImageFile || !nameTrim) {
    return { ok: false, message: 'Missing required fields.' };
  }

  const formData = new FormData();
  formData.append('token', token);
  formData.append('cover_image', coverImageFile);
  formData.append('name', nameTrim);

  return submitAdminForm(
    config.API_ENDPOINTS.ADMIN_CREATE_CATEGORY,
    formData,
    navigate,
    {
      logLabel: 'Error creating admin category:',
      successMessage: 'Category created successfully.',
      failureMessage: 'Failed to create category.',
    },
  );
};

/**
 * Updates a category (admin). Cover image is optional.
 * POST /api/admin/category/update — fields: token, id, cover_image?, name
 *
 * @param {{ id: string, coverImageFile?: File|null, name: string }} params
 * @param {function} navigate
 * @returns {Promise<{ ok: boolean, message?: string, data?: unknown }>}
 */
export const updateAdminCategory = async (
  { id, coverImageFile = null, name },
  navigate,
) => {
  const token = getToken();
  const idStr = String(id ?? '').trim();
  const nameTrim = String(name ?? '').trim();
  if (!token || !idStr || !nameTrim) {
    return { ok: false, message: 'Missing required fields.' };
  }

  const formData = new FormData();
  formData.append('token', token);
  formData.append('id', idStr);
  if (coverImageFile) {
    formData.append('cover_image', coverImageFile);
  }
  formData.append('name', nameTrim);

  return submitAdminForm(
    config.API_ENDPOINTS.ADMIN_UPDATE_CATEGORY,
    formData,
    navigate,
    {
      method: 'POST',
      logLabel: 'Error updating admin category:',
      successMessage: 'Category updated successfully.',
      failureMessage: 'Failed to update category.',
    },
  );
};

/**
 * Soft-deletes a category (admin).
 * POST /api/admin/category/delete — fields: token, id
 *
 * @param {{ id: string }} params
 * @param {function} navigate
 * @returns {Promise<{ ok: boolean, message?: string, data?: unknown }>}
 */
export const deleteAdminCategory = async ({ id }, navigate) => {
  const token = getToken();
  const idStr = String(id ?? '').trim();
  if (!token || !idStr) {
    return { ok: false, message: 'Missing required fields.' };
  }

  const formData = new FormData();
  formData.append('token', token);
  formData.append('id', idStr);

  return submitAdminForm(
    config.API_ENDPOINTS.ADMIN_DELETE_CATEGORY,
    formData,
    navigate,
    {
      method: 'POST',
      logLabel: 'Error deleting admin category:',
      successMessage: 'Category deleted successfully.',
      failureMessage: 'Failed to delete category.',
    },
  );
};

/**
 * Fetches content for a category (admin).
 * GET /api/admin/contents?token=...&category_id=...
 * @param {{ categoryId: string }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ categoryId: string, category: string, contents: Array }|null>}
 */
export const fetchAdminContents = async ({ categoryId }, navigate) => {
  const token = getToken();
  const categoryIdStr = String(categoryId ?? '').trim();
  if (!token || !categoryIdStr) return null;
  try {
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.ADMIN_GET_CONTENTS}?category_id=${encodeURIComponent(categoryIdStr)}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
    });
    if (handleApiResponseError(response, navigate, true)) {
      return null;
    }
    const data = await parseJsonResponse(response);
    if (!data?.success) return null;
    const contents = data?.data?.contents;
    return {
      categoryId: String(data?.data?.category_id ?? categoryIdStr),
      category: String(data?.data?.category ?? '').trim(),
      contents: Array.isArray(contents)
        ? contents.filter((item) => Number(item?.status ?? 1) === 1)
        : [],
    };
  } catch (error) {
    logger.error('Error fetching admin category content:', error);
    handleApiError(error, navigate, true);
    return null;
  }
};

/**
 * Creates category content (admin).
 * PUT /api/admin/content/create — fields: token, category_id, title, video, cover_image?, description?, actions?
 * @param {{ categoryId: string, coverImageFile?: File|null, videoFile: File, title: string, actions: string[], description?: string }} params
 * @param {function} navigate
 * @returns {Promise<{ ok: boolean, message?: string, data?: unknown }>}
 */
export const createAdminContent = async ({ categoryId, coverImageFile = null, videoFile, title, actions, description = '' }, navigate) => {
  const token = getToken();
  const categoryIdStr = String(categoryId ?? '').trim();
  const titleValue = String(title ?? '').trim();
  const descriptionValue = String(description ?? '');
  const actionsValue = Array.isArray(actions)
    ? actions.map((item) => String(item ?? '').trim()).filter(Boolean)
    : [];
  if (!token || !categoryIdStr || !videoFile) {
    return { ok: false, message: 'Missing required fields.' };
  }
  if (!titleValue) {
    return { ok: false, message: 'Title is required.' };
  }

  const formData = new FormData();
  formData.append('token', token);
  formData.append('category_id', categoryIdStr);
  if (coverImageFile) {
    formData.append('cover_image', coverImageFile);
  }
  formData.append('video', videoFile);
  formData.append('title', titleValue);
  formData.append('description', descriptionValue);
  formData.append('actions', JSON.stringify(actionsValue));

  return submitAdminForm(
    config.API_ENDPOINTS.ADMIN_CREATE_CONTENT,
    formData,
    navigate,
    {
      logLabel: 'Error creating admin category content:',
      successMessage: 'Quick Richuel created successfully.',
      failureMessage: 'Failed to create Quick Richuel.',
    },
  );
};

/**
 * Updates category content (admin).
 * POST /api/admin/content/update — fields: token, id, category_id, title, cover_image?, remove_cover?, video?, description?, actions?
 * @param {{ id: string, categoryId: string, coverImageFile?: File|null, removeCover?: boolean, videoFile?: File|null, title: string, actions: string[], description?: string }} params
 * @param {function} navigate
 * @returns {Promise<{ ok: boolean, message?: string, data?: unknown }>}
 */
export const updateAdminContent = async ({ id, categoryId, coverImageFile = null, removeCover = false, videoFile = null, title, actions, description = '' }, navigate) => {
  const token = getToken();
  const idStr = String(id ?? '').trim();
  const categoryIdStr = String(categoryId ?? '').trim();
  const titleValue = String(title ?? '').trim();
  const descriptionValue = String(description ?? '');
  const actionsValue = Array.isArray(actions)
    ? actions.map((item) => String(item ?? '').trim()).filter(Boolean)
    : [];
  if (!token || !idStr || !categoryIdStr) {
    return { ok: false, message: 'Missing required fields.' };
  }
  if (!titleValue) {
    return { ok: false, message: 'Title is required.' };
  }

  const formData = new FormData();
  formData.append('token', token);
  formData.append('id', idStr);
  formData.append('category_id', categoryIdStr);
  if (coverImageFile) {
    formData.append('cover_image', coverImageFile);
  } else if (removeCover) {
    formData.append('remove_cover', '1');
  }
  if (videoFile) {
    formData.append('video', videoFile);
  }
  formData.append('title', titleValue);
  formData.append('description', descriptionValue);
  formData.append('actions', JSON.stringify(actionsValue));

  return submitAdminForm(
    config.API_ENDPOINTS.ADMIN_UPDATE_CONTENT,
    formData,
    navigate,
    {
      method: 'POST',
      logLabel: 'Error updating admin category content:',
      successMessage: 'Quick Richuel updated successfully.',
      failureMessage: 'Failed to update Quick Richuel.',
    },
  );
};

/**
 * Soft-deletes category content (admin).
 * POST /api/admin/content/delete — fields: token, id
 * @param {{ id: string }} params
 * @param {function} navigate
 * @returns {Promise<{ ok: boolean, message?: string, data?: unknown }>}
 */
export const deleteAdminContent = async ({ id }, navigate) => {
  const token = getToken();
  const idStr = String(id ?? '').trim();
  if (!token || !idStr) {
    return { ok: false, message: 'Missing required fields.' };
  }

  const formData = new FormData();
  formData.append('token', token);
  formData.append('id', idStr);

  return submitAdminForm(
    config.API_ENDPOINTS.ADMIN_DELETE_CONTENT,
    formData,
    navigate,
    {
      method: 'POST',
      logLabel: 'Error deleting admin category content:',
      successMessage: 'Quick Richuel deleted successfully.',
      failureMessage: 'Failed to delete Quick Richuel.',
    },
  );
};

/**
 * Deletes an onboarding user (superadmin only).
 * POST /api/admin/user/delete — fields: token, username
 *
 * @param {{ username: string }} params
 * @param {function|null|undefined} navigate - For 401 / network error handling
 * @returns {Promise<{ ok: boolean, message?: string, data?: unknown }>}
 */
export const deleteAdminUser = async ({ username }, navigate) => {
  const token = getToken();
  const usernameStr = String(username ?? '').trim();
  if (!token || !usernameStr) {
    return { ok: false, message: 'Missing required fields.' };
  }

  const formData = new FormData();
  formData.append('token', token);
  formData.append('username', usernameStr);

  return submitAdminForm(
    config.API_ENDPOINTS.ADMIN_DELETE_USER,
    formData,
    navigate,
    {
      method: 'POST',
      logLabel: 'Error deleting admin user:',
      successMessage: 'User deleted successfully.',
      failureMessage: 'Failed to delete user.',
    },
  );
};

// API function to get onboarding AI data (for onboarding-agent)
// Optional signal (e.g. from AbortController) allows cancelling the request when component unmounts or effect re-runs (e.g. React Strict Mode)
export const getOnboardingAIData = async (token, navigate, isAdmin = false, signal = null) => {
  try {
    const response = await apiFetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.V2_ONBOARDING_AI}`, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }, token),
      ...(signal && { signal }),
    });

    // Handle response errors consistently
    if (handleApiResponseError(response, navigate, isAdmin)) {
      return null; // Error was handled, return null
    }

    const data = await response.json();

    return data;
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    logger.error('Error fetching onboarding AI data:', error);
    handleApiError(error, navigate, isAdmin);
    throw error;
  }
};

/**
 * Fetches onboarding AI users list (admin).
 * GET /api/admin/onboarding-ai/users?token=...
 *
 * @param {function|null|undefined} navigate
 * @param {AbortSignal|null} [signal]
 * @returns {Promise<{ ok: boolean, users: Array, message?: string }>}
 */
export const fetchAdminOnboardingUsers = async (navigate, signal = null) => {
  const token = getToken();
  if (!token) {
    return { ok: false, message: 'Not authenticated.', users: [] };
  }

  try {
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.ADMIN_ONBOARDING_AI_USERS}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      ...(signal && { signal }),
    });

    if (handleApiResponseError(response, navigate, true)) {
      return { ok: false, message: 'Session expired. Please sign in again.', users: [] };
    }

    const data = await response.json().catch(() => ({}));
    if (response.ok && data.success) {
      return { ok: true, users: data.data || [] };
    }
    return { ok: false, message: data.message || 'Failed to fetch onboarding users', users: [] };
  } catch (error) {
    if (error?.name === 'AbortError') {
      return { ok: false, message: 'Request cancelled.', users: [] };
    }
    logger.error('Error fetching onboarding users:', error);
    handleApiError(error, navigate, true);
    return { ok: false, message: 'Network error. Please try again.', users: [] };
  }
};

/**
 * Persists v2 onboarding AI result for the logged-in user or an admin-selected user.
 * PUT /api/v2/onboarding-ai/update-result or PUT /api/admin/onboarding-ai/update-result
 *
 * @param {{ token: string, v2OnboardingAiResult: object, username?: string|null, onboardingAiDone?: boolean, incomeSources?: object }} params
 * @param {function|null|undefined} navigate
 * @param {{ isAdmin?: boolean, signal?: AbortSignal|null }} [options]
 * @returns {Promise<{ ok: boolean, message?: string, data?: unknown }>}
 */
export const updateV2OnboardingAiResult = async (
  { token, v2OnboardingAiResult, username = null, onboardingAiDone = false, incomeSources },
  navigate,
  { isAdmin = false, signal = null } = {}
) => {
  if (!token) {
    return { ok: false, message: 'Not authenticated.' };
  }

  const useAdminEndpoint = Boolean(username);
  const endpoint = useAdminEndpoint
    ? config.API_ENDPOINTS.ADMIN_ONBOARDING_AI_UPDATE_RESULT
    : config.API_ENDPOINTS.V2_ONBOARDING_AI_UPDATE_RESULT;
  const onboardingAiDoneField = onboardingAiDone ? { onboarding_ai_done: true } : {};
  const incomeSourcesField = incomeSources === undefined ? {} : { income_sources: incomeSources };
  const body = useAdminEndpoint
    ? { token, username, v2_onboarding_ai_result: v2OnboardingAiResult, ...onboardingAiDoneField }
    : { token, v2_onboarding_ai_result: v2OnboardingAiResult, ...onboardingAiDoneField, ...incomeSourcesField };

  try {
    const response = await apiFetch(`${config.API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
      ...(signal && { signal }),
    });

    if (handleApiResponseError(response, navigate, isAdmin || useAdminEndpoint)) {
      return { ok: false, message: 'Session expired. Please sign in again.' };
    }

    const data = await response.json().catch(() => ({}));
    if (response.ok && data?.success !== false) {
      return { ok: true, message: data.message, data: data.data };
    }
    return { ok: false, message: data?.message || 'Failed to save result.' };
  } catch (error) {
    if (error?.name === 'AbortError') {
      return { ok: false, message: 'Request cancelled.' };
    }
    logger.error('Error updating onboarding AI result:', error);
    return { ok: false, message: error.message || 'Network error.' };
  }
};

/**
 * Lightweight patch: saves runway_display and longevity_display into
 * v2_onboarding_ai_result without resetting any other fields.
 * PUT /api/v2/onboarding-ai/patch-metrics or PUT /api/admin/onboarding-ai/patch-metrics
 *
 * @param {{ runwayDisplay: string, longevityDisplay: string, username?: string|null }} metrics
 * @param {function|null|undefined} navigate
 * @param {{ isAdmin?: boolean }} [options]
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export const patchV2OnboardingAiDisplayMetrics = async (
  { runwayDisplay, longevityDisplay, username = null },
  navigate,
  { isAdmin = false } = {}
) => {
  const token = getToken();
  if (!token) return { ok: false, message: 'Not authenticated.' };
  const useAdminEndpoint = Boolean(username);
  const endpoint = useAdminEndpoint
    ? config.API_ENDPOINTS.ADMIN_ONBOARDING_AI_PATCH_METRICS
    : config.API_ENDPOINTS.V2_ONBOARDING_AI_PATCH_METRICS;
  const body = useAdminEndpoint
    ? { token, username, runway_display: runwayDisplay, longevity_display: longevityDisplay }
    : { token, runway_display: runwayDisplay, longevity_display: longevityDisplay };
  try {
    const response = await apiFetch(`${config.API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    if (handleApiResponseError(response, navigate, isAdmin || useAdminEndpoint)) {
      return { ok: false, message: 'Session expired. Please sign in again.' };
    }
    const data = await response.json().catch(() => ({}));
    if (response.ok && data?.success !== false) {
      return { ok: true };
    }
    return { ok: false, message: data?.message || 'Failed to patch metrics.' };
  } catch (error) {
    logger.error('Error patching display metrics:', error);
    return { ok: false, message: error.message || 'Network error.' };
  }
};

/**
 * Marks onboarding confetti as hidden for the logged-in user.
 * PUT /api/user/hide-onboarding-confetti?token=...
 *
 * @returns {Promise<boolean>}
 */
export const hideOnboardingConfetti = () =>
  putTokenQueryFlag(config.API_ENDPOINTS.USER_HIDE_CONFETTI, 'Error hiding onboarding confetti:');

/**
 * Update a user preference via a one-click email action link.
 * PUT /api/user/preferences?action=...&token=...
 *
 * Supports action = "monthly-digest" (sets nudge_monthly_mode = true).
 * The token may come from the URL query string (email link) or from localStorage.
 *
 * @param {{ action: string, token: string }} params
 * @returns {Promise<{ ok: boolean, message: string }>}
 */
export const updateUserPreferences = async ({ action, token }) => {
  if (!token) return { ok: false, message: 'Not authenticated.' };
  try {
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.USER_UPDATE_PREFERENCES}?action=${encodeURIComponent(action)}`;
    const response = await apiFetch(url, {
      method: 'PUT',
      headers: getApiHeaders({}, token),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok && data?.success !== false) {
      return { ok: true, message: data?.message || 'Preference updated successfully.' };
    }
    return { ok: false, message: data?.message || 'Failed to update preference.' };
  } catch (error) {
    logger.error('Error updating user preferences:', error);
    return { ok: false, message: 'Network error. Please try again.' };
  }
};

/**
 * Records that the Walk Me Through tour auto-showed and was dismissed/finished.
 * PUT /api/user/walk-me-through-done?token=...
 *
 * Increments the user's auto-show count (capped at 5) and refreshes the
 * "last shown" timestamp, so the tour auto-shows again only after the backend's
 * cooldown window, up to 5 times total. Should only be called after an
 * auto-triggered showing — not after a manual "Take a Tour" replay.
 *
 * @returns {Promise<boolean>}
 */
export const markWalkMeThroughDone = () =>
  putTokenQueryFlag(config.API_ENDPOINTS.USER_WALK_ME_THROUGH_DONE, 'Error marking walk me through done:');

/**
 * Generates onboarding chart/table HTML fragments.
 * POST /api/v1/charts/generate
 *
 * @param {object} chartsPayload
 * @param {{ signal?: AbortSignal|null, navigate?: function|null, isAdmin?: boolean }} [options]
 * @returns {Promise<{ ok: boolean, fragments?: object|null, message?: string }>}
 */
export const generateOnboardingCharts = async (
  chartsPayload,
  { signal = null, navigate = null, isAdmin = false } = {}
) => {
  try {
    const response = await apiFetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.ONBOARDING_AI_CHART_GENERATOR}`, {
      method: 'POST',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(chartsPayload),
      ...(signal && { signal }),
    });

    if (handleApiResponseError(response, navigate, isAdmin)) {
      return { ok: false, message: 'Session expired. Please sign in again.', fragments: null };
    }
    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      logger.error('ONBOARDING_AI_CHART_GENERATOR error:', response.status, errText);
      return { ok: false, message: 'Failed to generate charts.', fragments: null };
    }

    const fragments = await response.json();
    return { ok: true, fragments };
  } catch (error) {
    if (error?.name === 'AbortError') {
      return { ok: false, message: 'Request cancelled.', fragments: null };
    }
    logger.error('ONBOARDING_AI_CHART_GENERATOR request failed:', error);
    return { ok: false, message: 'Network error. Please try again.', fragments: null };
  }
};

/**
 * Fetches PDF documents for a CFP session (admin).
 * GET /api/admin/cfp-session-documents?token=...&cfp_session_id=...
 *
 * @param {{ cfpSessionId: string }} params
 * @param {function|null|undefined} navigate
 * @param {AbortSignal|null} [signal]
 * @returns {Promise<{ ok: boolean, documents: Array, message?: string }>}
 */
export const fetchAdminUserCfpSessionDocuments = async ({ cfpSessionId }, navigate, signal = null) => {
  const token = getToken();
  const sessionId = String(cfpSessionId ?? '').trim();
  if (!token) {
    return { ok: false, message: 'Not authenticated.', documents: [] };
  }
  if (!sessionId) {
    return { ok: false, message: 'Session id is required.', documents: [] };
  }

  try {
    const params = new URLSearchParams({ cfp_session_id: sessionId });
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.ADMIN_GET_CFP_SESSION_DOCUMENTS}?${params.toString()}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      ...(signal && { signal }),
    });

    if (handleApiResponseError(response, navigate, true)) {
      return { ok: false, message: 'Session expired. Please sign in again.', documents: [] };
    }

    const data = await response.json().catch(() => ({}));
    if (response.ok && data.success) {
      return { ok: true, documents: data.data || [] };
    }
    return { ok: false, message: data.message || 'Failed to fetch documents', documents: [] };
  } catch (error) {
    if (error?.name === 'AbortError') {
      return { ok: false, message: 'Request cancelled.', documents: [] };
    }
    logger.error('Error fetching admin CFP session documents:', error);
    handleApiError(error, navigate, true);
    return { ok: false, message: 'Network error. Please try again.', documents: [] };
  }
};

/**
 * Fetches users with CFP session data (admin).
 * GET /api/admin/user-cfp-sessions?token=...
 *
 * @param {function|null|undefined} navigate
 * @param {AbortSignal|null} [signal]
 * @returns {Promise<{ ok: boolean, sessions: Array, message?: string }>}
 */
export const fetchAdminUserCfpSessions = async (navigate, signal = null) => {
  const token = getToken();
  if (!token) {
    return { ok: false, message: 'Not authenticated.', sessions: [] };
  }

  try {
    const url = `${config.API_BASE_URL}${config.API_ENDPOINTS.ADMIN_GET_USER_CFP_SESSIONS}`;
    const response = await apiFetch(url, {
      method: 'GET',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      ...(signal && { signal }),
    });

    if (handleApiResponseError(response, navigate, true)) {
      return { ok: false, message: 'Session expired. Please sign in again.', sessions: [] };
    }

    const data = await response.json().catch(() => ({}));
    if (response.ok && data.success) {
      return { ok: true, sessions: data.data || [] };
    }
    return { ok: false, message: data.message || 'Failed to fetch user CFP sessions', sessions: [] };
  } catch (error) {
    if (error?.name === 'AbortError') {
      return { ok: false, message: 'Request cancelled.', sessions: [] };
    }
    logger.error('Error fetching user CFP sessions:', error);
    handleApiError(error, navigate, true);
    return { ok: false, message: 'Network error. Please try again.', sessions: [] };
  }
};

/**
 * Marks a CFP session as done (admin).
 * POST /api/admin/cfp-session/done — form: token, id
 *
 * @param {{ id: string }} params
 * @param {function|null|undefined} navigate
 * @returns {Promise<{ ok: boolean, message?: string, data?: unknown }>}
 */
export const markAdminCfpSessionDone = async ({ id }, navigate) => {
  const token = getToken();
  const idStr = String(id ?? '').trim();
  if (!token || !idStr) {
    return { ok: false, message: 'Missing required fields.' };
  }

  const formData = new FormData();
  formData.append('token', token);
  formData.append('id', idStr);

  return submitAdminForm(
    config.API_ENDPOINTS.ADMIN_MARK_CFP_SESSION_DONE,
    formData,
    navigate,
    {
      method: 'POST',
      logLabel: 'Error marking admin CFP session as done:',
      successMessage: 'CFP session marked as done successfully.',
      failureMessage: 'Failed to mark CFP session as done.',
    },
  );
};